import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/authContext";
import { useLoading } from "../../context/loadingContext";
import { isSuccessCode } from "../../helpers/apiResponse";
import * as EventAPI from "../../api/event";
import * as ReportAPI from "../../api/report";
import TableComponent from "../../component/form/TableComponent";
import { Column, TableButtons } from "../../types/TableComponent.types";
import { EventItem } from "../../types/api/event.type";
import { ReportItem } from "../../types/api/report.type";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { LoadingScreenLocal } from "../other/LoadingScreen";
import {
    countPendingReportsForMember,
    isCurrentEvent,
    isPastEvent,
    toEventItem,
    toReportItem,
    todayDateOnly
} from "./reportOverview.helpers";
import "./ReportsOverviewScreen.css";

const ReportsOverviewScreen: React.FC = () => {
    const { association, associationRole, user } = useAuth();
    const { loading, setLoadingStatus } = useLoading();
    const nav = useNavigate();

    const [events, setEvents] = useState<EventItem[]>([]);
    const [reports, setReports] = useState<ReportItem[]>([]);
    const [pastPage, setPastPage] = useState(0);
    const [pastLimit, setPastLimit] = useState(10);

    const associationId = String(association.id);
    const today = useMemo(() => todayDateOnly(), []);
    const isAthleteView = associationRole === "athlete" || associationRole === "parent";

    const handleApiError = useCallback((requestId: string, errorCode: string) => {
        nav(`/500?process_id=${requestId}&error_code=${errorCode}`);
    }, [nav]);

    const loadData = useCallback(async () => {
        const [eventsResponse, reportsResponse] = await Promise.all([
            EventAPI.list(associationId),
            ReportAPI.list(associationId),
        ]);

        if (!isSuccessCode(eventsResponse.error_code)) {
            return handleApiError(eventsResponse.request_id, eventsResponse.error_code);
        }
        if (!isSuccessCode(reportsResponse.error_code)) {
            return handleApiError(reportsResponse.request_id, reportsResponse.error_code);
        }

        const mappedEvents = (Array.isArray(eventsResponse.data) ? eventsResponse.data : [])
            .map((item: Record<string, unknown>) => toEventItem(item))
            .filter((event) => event.id > 0)
            .sort((a, b) => b.start_date.localeCompare(a.start_date));
        const mappedReports = (Array.isArray(reportsResponse.data) ? reportsResponse.data : [])
            .map((item: Record<string, unknown>) => toReportItem(item))
            .filter((report) => report.id > 0);

        setEvents(mappedEvents);
        setReports(mappedReports);
    }, [associationId, handleApiError]);

    useEffect(() => {
        setLoadingStatus(true);
        loadData().finally(() => setLoadingStatus(false));
    }, [loadData, setLoadingStatus]);

    const currentEvents = useMemo(() => events.filter((event) => isCurrentEvent(event, today)), [events, today]);
    const pastEvents = useMemo(() => events.filter((event) => isPastEvent(event, today)), [events, today]);
    const pagedPastEvents = useMemo(
        () => pastEvents.slice(pastPage * pastLimit, (pastPage + 1) * pastLimit),
        [pastEvents, pastLimit, pastPage]
    );

    const currentRows = useMemo(() => {
        if (isAthleteView) {
            return currentEvents.map((event) => ({
                ...event,
                pending: countPendingReportsForMember(event, reports, user.id, today),
            }));
        }
        return currentEvents.map((event) => ({
            ...event,
            total_reports: reports.filter((report) => report.event_id === event.id && report.status === "submitted").length,
        }));
    }, [currentEvents, isAthleteView, reports, today, user.id]);

    const pastRows = useMemo(() => {
        if (isAthleteView) {
            return pagedPastEvents.map((event) => ({
                ...event,
                pending: countPendingReportsForMember(event, reports, user.id, today),
            }));
        }
        return pagedPastEvents.map((event) => ({
            ...event,
            total_reports: reports.filter((report) => report.event_id === event.id && report.status === "submitted").length,
        }));
    }, [isAthleteView, pagedPastEvents, reports, today, user.id]);

    const eventButtons: TableButtons[] = [
        {
            button: <FontAwesomeIcon icon={faEye} title="Open event reports" />,
            bc: "green",
            placeholder: "Open",
            action: { param: "id", function: (eventId: number) => nav(`/report/event?event_id=${eventId}`) }
        }
    ];

    const currentColumns: Column[] = isAthleteView
        ? [
            { header: "Event", accessor: "name" },
            { header: "Start", accessor: "start_date" },
            { header: "End", accessor: "end_date" },
            { header: "Pending reports", accessor: "pending" },
        ]
        : [
            { header: "Event", accessor: "name" },
            { header: "Start", accessor: "start_date" },
            { header: "End", accessor: "end_date" },
            { header: "Compiled reports", accessor: "total_reports" },
        ];

    if (loading) {
        return <LoadingScreenLocal />;
    }

    console.log(events);
    

    return (
        <div className="reports-overview-screen">
            <h2>Current events</h2>
            <TableComponent data={currentRows} columns={currentColumns} buttons={eventButtons} />

            <h2>Past events</h2>
            <TableComponent
                data={pastRows}
                columns={currentColumns}
                buttons={eventButtons}
                pages={{
                    enabled: true,
                    page: pastPage,
                    limit: pastLimit,
                    onPageChange: (newPage: number) => setPastPage(Math.max(0, newPage)),
                    onLimitChange: (newLimit: number) => {
                        setPastLimit(newLimit);
                        setPastPage(0);
                    },
                }}
            />
        </div>
    );
};

export default ReportsOverviewScreen;
